import { Vector3 } from 'three';

import type { AnimationManager } from '../../../three-helpers/animation';
import { easeOutCubic } from '../../../three-helpers/animation/easing';
import { interpolateAlongPath } from '../../../three-helpers/animation/path-interpolation';
import type { CameraManager } from '../../managers';
import type { DebugManager } from '../../managers';
import type { KitchenDebugData } from '../../types';
import {
	boundsToVectors,
	calculateAutoZoomPosition,
	calculateCameraGeometry,
	calculateSceneBoundsMetrics,
	cloneBounds,
	computePresentationPoint
} from '../functions';
import {
	ARC_SOURCE_Y_OFFSET,
	AUTO_ZOOM_PADDING,
	CAMERA_MIN_DISTANCE,
	DEFAULT_CAMERA_XZ_DISTANCE,
	DEFAULT_CAMERA_Y
} from '../types/bounds-types';

/** @see SceneCameraDelegateConfig.autoZoomStyle */
export type AutoZoomStyle = 'slide' | 'settle';

export type SceneCameraDelegateConfig = {
	cameraManager: CameraManager | null;
	debugManager: DebugManager | null;
	animationManager: AnimationManager | null;
	/** Start with the presentation auto-zoom enabled (default `true`). */
	autoZoomEnabled?: boolean;
	/**
	 * Bounds padding the auto-zoom frames to (default `AUTO_ZOOM_PADDING`). The
	 * scene's bounding box is scaled by this before the camera is fitted to it,
	 * so a smaller number frames the kitchen closer.
	 */
	autoZoomPadding?: number;
	/**
	 * Raises the arc the auto-zoom camera slides along, as a fraction of its
	 * height above the look-at point (default 0). 0.15 puts the resting camera
	 * roughly 15% higher, for a slightly more top-down view.
	 */
	autoZoomCameraHeightBoost?: number;
	/**
	 * How the auto-zoom reaches its framing (default `'slide'`).
	 * - `'slide'`: the presentation flourish — jump to the arc start, then
	 *   animate in once the scene has finished building.
	 * - `'settle'`: frame the scene the moment its bounds are known, before any
	 *   geometry renders, and stay there. The scene then assembles inside its
	 *   final framing with no camera move at the end.
	 */
	autoZoomStyle?: AutoZoomStyle;
};


/**
 * Delegate that owns camera positioning, auto-zoom, and presentation point state.
 * Extracted from ThreeSceneEngine to reduce file size.
 */
export class SceneCameraDelegate {
	#cameraManager: CameraManager | null;
	#debugManager: DebugManager | null;
	#animationManager: AnimationManager | null;

	// Camera state
	#defaultCameraPosition: Vector3 | null = null;
	#defaultCameraTarget: Vector3 | null = null;
	#cameraVectorTarget: Vector3 | null = null;

	// Auto-zoom state
	#autoZoomEnabled = true;
	#autoZoomPadding: number;
	#autoZoomCameraHeightBoost: number;
	#autoZoomStyle: AutoZoomStyle;
	#autoZoomPosition: Vector3 | null = null;
	#autoZoomArcPath: Array<Vector3> | null = null;
	#autoZoomArcTangent: Vector3 | null = null;

	// Presentation point
	#defaultPresentationPoint: Vector3 | null = null;

	// Scene bounds
	#lastSceneBounds: { min: Vector3; max: Vector3 } | null = null;

	constructor (config: SceneCameraDelegateConfig) {
		this.#cameraManager = config.cameraManager;
		this.#debugManager = config.debugManager;
		this.#animationManager = config.animationManager;
		this.#autoZoomEnabled = config.autoZoomEnabled ?? true;
		this.#autoZoomPadding = config.autoZoomPadding ?? AUTO_ZOOM_PADDING;
		this.#autoZoomCameraHeightBoost = config.autoZoomCameraHeightBoost ?? 0;
		this.#autoZoomStyle = config.autoZoomStyle ?? 'slide';
	}

	get autoZoomStyle (): AutoZoomStyle {
		return this.#autoZoomStyle;
	}


	// ========== Public Getters (read by other delegates) ==========

	get defaultCameraPosition (): Vector3 | null {
		return this.#defaultCameraPosition;
	}

	get defaultCameraTarget (): Vector3 | null {
		return this.#defaultCameraTarget;
	}

	get cameraVectorTarget (): Vector3 | null {
		return this.#cameraVectorTarget;
	}

	get lastSceneBounds (): { min: Vector3; max: Vector3 } | null {
		return this.#lastSceneBounds;
	}

	get autoZoomPosition (): Vector3 | null {
		return this.#autoZoomPosition?.clone() ?? null;
	}

	get autoZoomArcPath (): Array<Vector3> | null {
		return this.#autoZoomArcPath;
	}

	get autoZoomArcTangent (): Vector3 | null {
		return this.#autoZoomArcTangent;
	}

	get defaultPresentationPoint (): Vector3 | null {
		return this.#defaultPresentationPoint;
	}

	get sceneCenter (): Vector3 | null {
		return this.getCurrentPresentationPoint() ?? this.#defaultPresentationPoint;
	}

	set autoZoomEnabled (enabled: boolean) {
		this.#autoZoomEnabled = enabled;
	}

	get isAutoZoomEnabled (): boolean {
		return this.#autoZoomEnabled;
	}

	// ========== Setters (used by composition delegate) ==========

	set lastSceneBounds (bounds: { min: Vector3; max: Vector3 } | null) {
		this.#lastSceneBounds = bounds;
	}

	set defaultCameraPosition (pos: Vector3 | null) {
		this.#defaultCameraPosition = pos;
	}

	set defaultCameraTarget (target: Vector3 | null) {
		this.#defaultCameraTarget = target;
	}

	set cameraVectorTarget (target: Vector3 | null) {
		this.#cameraVectorTarget = target;
	}

	set defaultPresentationPoint (point: Vector3 | null) {
		this.#defaultPresentationPoint = point;
	}

	// ========== Manager Updates ==========

	updateManagers (config: Partial<SceneCameraDelegateConfig>): void {
		if (config.cameraManager !== undefined) this.#cameraManager = config.cameraManager;
		if (config.debugManager !== undefined) this.#debugManager = config.debugManager;
		if (config.animationManager !== undefined) this.#animationManager = config.animationManager;
	}

	// ========== Methods ==========

	/**
	 * Set default presentation point and camera position from kitchen data.
	 * Must be called BEFORE progressive rendering.
	 */
	setSceneCenterFromKitchenData (data: KitchenDebugData, skipPositioning = false): void {
		const { origin, size, left, right, defaultIslandX = 0 } = data;

		// Validate required data - use fallback if invalid
		if (!size || !size.width || !size.height || !size.depth) {
			console.warn('SceneCameraDelegate.setSceneCenterFromKitchenData: Invalid size data, using fallback camera position');
			const fallbackCameraPos = new Vector3(0, 104, 400);
			const fallbackTarget = new Vector3(0, 18, 0);
			this.#defaultCameraPosition = fallbackCameraPos.clone();
			this.#defaultCameraTarget = fallbackTarget.clone();
			if (this.#cameraManager && !skipPositioning) {
				this.#cameraManager.setPosition(fallbackCameraPos.x, fallbackCameraPos.y, fallbackCameraPos.z, fallbackTarget.x, fallbackTarget.y, fallbackTarget.z, false);
			}
			return;
		}

		// Calculate circle parameters
		const circleCenterX = origin?.x ?? 0;
		const circleCenterZ = origin?.z ?? 0;
		const circleY = size.height;
		const circleRadius = size.width / 2;

		// Calculate Z positions for left/right sections
		let leftZ = circleCenterZ - circleRadius;
		let rightZ = circleCenterZ + circleRadius;

		if (left?.origin && left?.size) {
			leftZ = left.origin.z + (left.size.depth / 2);
		}
		if (right?.origin && right?.size) {
			rightZ = right.origin.z + (right.size.depth / 2);
		}

		// Project Z positions onto the circle
		const leftZDelta = leftZ - circleCenterZ;
		const leftXOffset = Math.sqrt(Math.max(0, circleRadius * circleRadius - leftZDelta * leftZDelta));
		const rightZDelta = rightZ - circleCenterZ;
		const rightXOffset = Math.sqrt(Math.max(0, circleRadius * circleRadius - rightZDelta * rightZDelta));

		// Calculate angles for midpoint calculation
		const leftZPos = new Vector3(circleCenterX - leftXOffset, circleY, leftZ);
		const rightZPos = new Vector3(circleCenterX + rightXOffset, circleY, rightZ);

		// Calculate default island position on circle
		const xDelta = defaultIslandX - circleCenterX;
		const zOffset = Math.sqrt(Math.max(0, circleRadius * circleRadius - xDelta * xDelta));
		const defaultIslandZ = circleCenterZ - zOffset;
		const blueSpherePos = new Vector3(defaultIslandX, circleY, defaultIslandZ);

		const getAngle = (pos: Vector3): number => {
			const dx = pos.x - circleCenterX;
			const dz = pos.z - circleCenterZ;
			return Math.atan2(dz, dx);
		};

		const blueSphereAngle = getAngle(blueSpherePos);
		const leftZAngle = getAngle(leftZPos);
		const rightZAngle = getAngle(rightZPos);

		// Calculate midpoint angle
		const normalizeAngle = (a: number): number => ((a % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
		const nBlue = normalizeAngle(blueSphereAngle);
		const nLeft = normalizeAngle(leftZAngle);
		const nRight = normalizeAngle(rightZAngle);

		const isAngleBetween = (angle: number, start: number, end: number): boolean => {
			if (start <= end) return angle >= start && angle <= end;
			return angle >= start || angle <= end;
		};

		let midpointAngle: number;
		const arc1Midpoint = (nLeft + nRight) / 2;
		const arc2Midpoint = normalizeAngle(arc1Midpoint + Math.PI);

		if (isAngleBetween(nBlue, Math.min(nLeft, nRight), Math.max(nLeft, nRight))) {
			midpointAngle = arc1Midpoint;
		} else {
			midpointAngle = arc2Midpoint;
		}

		// Camera position (opposite side from target) at arc-start height above kitchen
		const cameraDistance = DEFAULT_CAMERA_XZ_DISTANCE;
		const cameraAngle = midpointAngle + Math.PI;
		const cameraPosition = new Vector3(
			circleCenterX + Math.cos(cameraAngle) * cameraDistance,
			circleY + ARC_SOURCE_Y_OFFSET,
			circleCenterZ + Math.sin(cameraAngle) * cameraDistance
		);

		// Camera vector target: the point on the kitchen circle the camera aims at
		const cameraVectorTarget = new Vector3(
			circleCenterX + Math.cos(midpointAngle) * circleRadius,
			circleY,
			circleCenterZ + Math.sin(midpointAngle) * circleRadius
		);

		// Orbit pivot: kitchen center at mid-height
		const orbitPivot = new Vector3(
			origin?.x ?? 0,
			circleY / 2,
			origin?.z ?? 0
		);

		// Store camera position, orbit target, and vector target
		this.#defaultCameraPosition = cameraPosition.clone();
		this.#defaultCameraTarget = orbitPivot.clone();
		this.#cameraVectorTarget = cameraVectorTarget.clone();

		// Initially aim the camera at the vector target (skip when resetting view)
		if (this.#cameraManager && !skipPositioning) {
			this.#cameraManager.setPosition(
				cameraPosition.x,
				cameraPosition.y,
				cameraPosition.z,
				cameraVectorTarget.x,
				cameraVectorTarget.y,
				cameraVectorTarget.z,
				false
			);
		}
	}

	/**
	 * Put the camera straight on its auto-zoom framing, with no animation.
	 *
	 * Called before the geometry renders (the builder reports scene bounds up
	 * front) so a progressive build assembles inside the framing it will end on
	 * — instead of being watched from the foundation's default camera and then
	 * jumping when the closing flourish runs. Called again once the scene is
	 * complete, which corrects for any drift between the announced bounds and
	 * the built ones.
	 */
	settleAutoZoom (bounds?: { min: { x: number; y: number; z: number }; max: { x: number; y: number; z: number } }): void {
		if (!this.#cameraManager) return;

		if (bounds) {
			const { min, max } = boundsToVectors(bounds);
			this.#lastSceneBounds = cloneBounds({ min, max });
			// Seeds #defaultCameraPosition / #defaultCameraTarget when the scene
			// has not been pre-positioned from kitchen data.
			if (!this.#defaultCameraPosition || !this.#defaultCameraTarget) {
				this.calculateCameraFromBounds(bounds, false);
			}
		}

		if (!this.#lastSceneBounds || !this.#defaultCameraPosition || !this.#defaultCameraTarget) return;

		const { camera } = this.#cameraManager;
		const { min, max } = this.#lastSceneBounds;
		const vectorTarget = this.#cameraVectorTarget ?? this.#defaultCameraTarget;

		const arcSource = new Vector3(
			this.#defaultCameraPosition.x,
			max.y + ARC_SOURCE_Y_OFFSET,
			this.#defaultCameraPosition.z
		);

		const autoZoomResult = calculateAutoZoomPosition({
			source: arcSource,
			target: vectorTarget,
			boundsMin: min,
			boundsMax: max,
			camera,
			padding: this.#autoZoomPadding,
			cameraHeightBoost: this.#autoZoomCameraHeightBoost,
			viewportAnchor: this.#cameraManager.viewportAnchor
		});

		this.#cameraManager.setOptimalDistanceValue(autoZoomResult.distance);
		this.#autoZoomPosition = autoZoomResult.arcPosition.clone();
		this.#defaultPresentationPoint = autoZoomResult.presentationPoint.clone();
		this.#cameraManager.setCameraVector(arcSource, vectorTarget);
		this.#cameraManager.setDistanceConstraints(CAMERA_MIN_DISTANCE, arcSource.distanceTo(vectorTarget));

		const restingPosition = autoZoomResult.arcPosition;
		this.#cameraManager.setPosition(
			restingPosition.x,
			restingPosition.y,
			restingPosition.z,
			vectorTarget.x,
			vectorTarget.y,
			vectorTarget.z,
			false
		);
	}

	/**
	 * Get the current presentation point (48" from auto-zoom position toward target).
	 */
	getCurrentPresentationPoint (): Vector3 | null {
		// Prefer auto-zoom position as the basis for presentation point
		const vectorTarget = this.#cameraVectorTarget ?? this.#defaultCameraTarget;
		if (this.#autoZoomPosition && vectorTarget) {
			return computePresentationPoint(this.#autoZoomPosition, vectorTarget);
		}

		const camera = this.#cameraManager?.camera;
		if (!camera) {
			return this.#defaultPresentationPoint;
		}

		// Fallback: use active camera position
		const cameraPos = camera.position.clone();

		// Get camera target from controls or use default target
		const controls = this.#cameraManager?.controls;
		let targetPos: Vector3;

		if (controls) {
			targetPos = new Vector3();
			controls.getTarget(targetPos);
		} else if (this.#defaultCameraTarget) {
			targetPos = this.#defaultCameraTarget.clone();
		} else {
			// Fallback: look forward along camera's -Z axis
			targetPos = cameraPos.clone().add(new Vector3(0, 0, -100).applyQuaternion(camera.quaternion));
		}

		// Calculate direction from camera to target
		const direction = targetPos.clone().sub(cameraPos)
			.normalize();

		// Current presentation point: 48" (4') from camera along view direction
		return cameraPos.clone().add(direction.multiplyScalar(48));
	}

	/**
	 * Perform auto-zoom: calculate the optimal camera position along the
	 * source-to-target vector where the kitchen fills the viewport with
	 * 5% padding, then animate the camera sliding from source to that position.
	 */
	async performAutoZoom (animate = true, duration = 2000): Promise<void> {
		if (!this.#cameraManager || !this.#lastSceneBounds) return;
		if (!this.#defaultCameraPosition || !this.#defaultCameraTarget) return;

		const { camera } = this.#cameraManager;
		const { min, max } = this.#lastSceneBounds;

		// The camera vector target defines the zoom vector direction
		const vectorTarget = this.#cameraVectorTarget ?? this.#defaultCameraTarget;

		// Elevate the source to the arc start position
		const arcSource = new Vector3(
			this.#defaultCameraPosition.x,
			max.y + ARC_SOURCE_Y_OFFSET,
			this.#defaultCameraPosition.z
		);
		this.#defaultCameraPosition = arcSource.clone();

		// Store the camera vector on CameraManager
		this.#cameraManager.setCameraVector(arcSource, vectorTarget);

		// Calculate the auto-zoom position along the arc
		const autoZoomResult = calculateAutoZoomPosition({
			source: arcSource,
			target: vectorTarget,
			boundsMin: min,
			boundsMax: max,
			camera,
			padding: this.#autoZoomPadding,
			cameraHeightBoost: this.#autoZoomCameraHeightBoost,
			viewportAnchor: this.#cameraManager.viewportAnchor
		});

		// Set distance constraints
		const totalDistance = arcSource.distanceTo(vectorTarget);
		this.#cameraManager.setDistanceConstraints(CAMERA_MIN_DISTANCE, totalDistance);

		// Store auto-zoom position on the arc and distance
		this.#autoZoomPosition = autoZoomResult.arcPosition.clone();
		this.#cameraManager.setOptimalDistanceValue(autoZoomResult.distance);

		// Store arc path for debug visualization
		this.#autoZoomArcPath = autoZoomResult.arcPath;
		this.#autoZoomArcTangent = autoZoomResult.arcTangent.clone();

		// Presentation point: 48" from arc auto-zoom position toward camera target
		this.#defaultPresentationPoint = autoZoomResult.presentationPoint.clone();

		// Add debug shapes at auto-zoom and presentation positions
		if (this.#debugManager) {
			// Update camera position sphere to match arc start
			this.#debugManager.addSphere({
				id: 'camera-position',
				position: arcSource,
				radius: 2,
				color: 0x00ffff,
				label: 'Camera Position'
			});

			const arcTangentNorm = autoZoomResult.arcTangent.clone()
				.normalize();

			// Red arc path from elevated source to target
			this.#debugManager.addPath({
				id: 'camera-to-target-arc',
				points: autoZoomResult.arcPath,
				color: 0xff0000
			});

			// Orange cylinder at auto-zoom position on the arc
			this.#debugManager.addCylinder({
				id: 'auto-zoom-position',
				position: this.#autoZoomPosition,
				radius: 2,
				height: 4,
				color: 0xff8800,
				label: 'Auto-Zoom Position',
				axis: arcTangentNorm
			});

			// Orange line from auto-zoom position to presentation point
			this.#debugManager.addLine({
				id: 'presentation-line',
				start: this.#autoZoomPosition,
				end: this.#defaultPresentationPoint!,
				color: 0xff8800
			});

			// Yellow disc at presentation point
			this.#debugManager.addDisc({
				id: 'default-presentation-point',
				position: this.#defaultPresentationPoint!,
				radius: 3,
				color: 0xffff00,
				label: 'Default Presentation Point',
				normal: arcTangentNorm
			});
		}

		if (animate && this.#animationManager) {
			const animationId = 'camera-auto-zoom-slide';
			const { arcPath } = autoZoomResult;

			// Position camera at the arc start before animating. Read it off the
			// path rather than from arcSource so a height-boosted arc starts
			// where the animation's first frame will be, with no jump.
			const arcStart = arcPath[0] ?? arcSource;
			this.#cameraManager.setPosition(arcStart.x, arcStart.y, arcStart.z, vectorTarget.x, vectorTarget.y, vectorTarget.z, false);

			const lookAtTarget = vectorTarget.clone();

			// Cancel animation if user interacts
			const cancelHandler = (): void => {
				if (this.#animationManager?.isRunning(animationId)) {
					this.#animationManager.stop(animationId);
				}
			};
			const { controls } = this.#cameraManager;
			controls.addEventListener('controlstart', cancelHandler);

			// Animate along the arc path
			const targetT = autoZoomResult.t;

			await new Promise<void>((resolve) => {
				this.#animationManager!.start({
					id: animationId,
					duration,
					easing: easeOutCubic,
					onUpdate: (_rawProgress, easedProgress) => {
						const arcProgress = easedProgress * targetT;
						const pos = interpolateAlongPath(arcPath, arcProgress);
						this.#cameraManager!.setPosition(pos.x, pos.y, pos.z, lookAtTarget.x, lookAtTarget.y, lookAtTarget.z, false);
					},
					onComplete: () => {
						controls.removeEventListener('controlstart', cancelHandler);
						resolve();
					}
				});
			});

			// Clean up in case animation was cancelled before completing
			controls.removeEventListener('controlstart', cancelHandler);
		} else {
			// Instant positioning at arc auto-zoom point
			const arcPos = autoZoomResult.arcPosition;
			this.#cameraManager.setPosition(
				arcPos.x,
				arcPos.y,
				arcPos.z,
				vectorTarget.x,
				vectorTarget.y,
				vectorTarget.z,
				false
			);
		}
	}

	/**
	 * Recalculate auto-zoom state and smoothly animate the camera from its
	 * current position to the new auto-zoom position.  Unlike performAutoZoom
	 * (which jumps to the arc source and slides inward), this method keeps the
	 * camera where it is and transitions via camera-controls' built-in easing.
	 */
	async resetAutoZoom (): Promise<void> {
		if (!this.#cameraManager || !this.#lastSceneBounds) return;
		if (!this.#defaultCameraPosition || !this.#defaultCameraTarget) return;

		const { camera } = this.#cameraManager;
		const { min, max } = this.#lastSceneBounds;
		const vectorTarget = this.#cameraVectorTarget ?? this.#defaultCameraTarget;

		// Elevate the source to the arc start position
		const arcSource = new Vector3(
			this.#defaultCameraPosition.x,
			max.y + ARC_SOURCE_Y_OFFSET,
			this.#defaultCameraPosition.z
		);
		this.#defaultCameraPosition = arcSource.clone();

		// Store the camera vector on CameraManager
		this.#cameraManager.setCameraVector(arcSource, vectorTarget);

		// Calculate the auto-zoom position along the arc
		const autoZoomResult = calculateAutoZoomPosition({
			source: arcSource,
			target: vectorTarget,
			boundsMin: min,
			boundsMax: max,
			camera,
			padding: this.#autoZoomPadding,
			cameraHeightBoost: this.#autoZoomCameraHeightBoost,
			viewportAnchor: this.#cameraManager.viewportAnchor
		});

		// Set distance constraints
		const totalDistance = arcSource.distanceTo(vectorTarget);
		this.#cameraManager.setDistanceConstraints(CAMERA_MIN_DISTANCE, totalDistance);

		// Store auto-zoom state
		this.#autoZoomPosition = autoZoomResult.arcPosition.clone();
		this.#cameraManager.setOptimalDistanceValue(autoZoomResult.distance);
		this.#autoZoomArcPath = autoZoomResult.arcPath;
		this.#autoZoomArcTangent = autoZoomResult.arcTangent.clone();
		this.#defaultPresentationPoint = autoZoomResult.presentationPoint.clone();

		// Update debug shapes
		if (this.#debugManager) {
			this.#debugManager.addSphere({
				id: 'camera-position',
				position: arcSource,
				radius: 2,
				color: 0x00ffff,
				label: 'Camera Position'
			});

			const arcTangentNorm = autoZoomResult.arcTangent.clone().normalize();

			this.#debugManager.addPath({
				id: 'camera-to-target-arc',
				points: autoZoomResult.arcPath,
				color: 0xff0000
			});

			this.#debugManager.addCylinder({
				id: 'auto-zoom-position',
				position: this.#autoZoomPosition,
				radius: 2,
				height: 4,
				color: 0xff8800,
				label: 'Auto-Zoom Position',
				axis: arcTangentNorm
			});

			this.#debugManager.addLine({
				id: 'presentation-line',
				start: this.#autoZoomPosition,
				end: this.#defaultPresentationPoint!,
				color: 0xff8800
			});

			this.#debugManager.addDisc({
				id: 'default-presentation-point',
				position: this.#defaultPresentationPoint!,
				radius: 3,
				color: 0xffff00,
				label: 'Default Presentation Point',
				normal: arcTangentNorm
			});
		}

		// Smoothly animate from current camera position to auto-zoom position
		const arcPos = autoZoomResult.arcPosition;
		this.#cameraManager.setPosition(
			arcPos.x, arcPos.y, arcPos.z,
			vectorTarget.x, vectorTarget.y, vectorTarget.z,
			true
		);
	}

	/**
	 * Apply bounds and highlight circle from composition data.
	 * Used by composition delegate when applying camera/bounds settings.
	 */
	applyBoundsAndHighlight (
		bounds: { min: { x: number; y: number; z: number }; max: { x: number; y: number; z: number } },
		highlightManager: { setCircle: (config: { centerX: number; centerZ: number; y: number; radius: number }) => void } | null
	): void {
		const { min, max } = boundsToVectors(bounds);
		this.#lastSceneBounds = cloneBounds({ min, max });

		const metrics = calculateSceneBoundsMetrics(min, max);

		if (highlightManager) {
			highlightManager.setCircle({
				centerX: metrics.centerX,
				centerZ: metrics.centerZ,
				y: metrics.highlightCircleY,
				radius: metrics.highlightCircleRadius
			});
		}
	}

	/**
	 * Calculate and set camera position from bounds (fallback when kitchen data not available).
	 */
	calculateCameraFromBounds (
		bounds: { min: { x: number; y: number; z: number }; max: { x: number; y: number; z: number } },
		animated: boolean
	): void {
		if (!this.#cameraManager) return;

		const { min, max } = boundsToVectors(bounds);
		this.#lastSceneBounds = cloneBounds({ min, max });

		const metrics = calculateSceneBoundsMetrics(min, max);

		const cameraGeometry = calculateCameraGeometry({
			centerX: metrics.centerX,
			centerZ: metrics.centerZ,
			circleRadius: metrics.kitchenCircleRadius,
			cameraY: DEFAULT_CAMERA_Y,
			xzDistance: DEFAULT_CAMERA_XZ_DISTANCE
		});

		this.#defaultCameraPosition = cameraGeometry.position.clone();
		this.#defaultCameraTarget = cameraGeometry.target.clone();

		this.#cameraManager.setPosition(
			cameraGeometry.position.x,
			cameraGeometry.position.y,
			cameraGeometry.position.z,
			cameraGeometry.target.x,
			cameraGeometry.target.y,
			cameraGeometry.target.z,
			animated
		);
	}
}
